import React, { useState, useEffect, useRef, forwardRef } from "react";
import apiInstance from "../../apiInstance";
import Button from "../../Button";
import PopupModal from "./PopupModal";

const AddOrderPopup = forwardRef(({ closePopup, addOrder }, ref) => {
  const popupModalRef = useRef(null);

  useEffect(() => {
    if (ref) {
      popupModalRef.current = ref.current;
    }
  }, [ref]);

  // Form state management
  const [orderData, setOrderData] = useState({
    title: "",
    widgetType: "",
    userId: "",
    expires: "",
    subStatus: ""
  });

  // Data for dropdowns
  const [availableWidgets, setAvailableWidgets] = useState([]);
  const [availableUsers, setAvailableUsers] = useState([]);

  // Validation errors
  const [fieldErrors, setFieldErrors] = useState({});

  // Fetch initial data on component mount
  useEffect(() => {
    const fetchInitialData = async () => {
      try {
        // Fetch available widgets
        const widgetsResponse = await apiInstance.get(
          "/devadmin/availableWidget"
        );
        setAvailableWidgets(widgetsResponse.data.availableWidgets || []);

        // Fetch available users
        const usersResponse = await apiInstance.get("/devadmin/user");
        setAvailableUsers(usersResponse.data.users || []);
      } catch (error) {
        console.error("Error fetching initial data:", error);
      }
    };

    fetchInitialData();
  }, []);

  // Handle form input changes
  const handleInputChange = (e) => {
    const { name, value } = e.target;
    setOrderData((prev) => ({
      ...prev,
      [name]: value,
    }));
  };

  // Validate form fields before submission
  const validateForm = () => {
    const errors = {};
    if (!orderData.title.trim()) {
      errors.title = "Widget Name is required.";
    }
    if (!orderData.widgetType) {
      errors.widgetType = "Widget Type is required.";
    }
    if (!orderData.userId) {
      errors.userId = "User is required.";
    }
    if (!orderData.subStatus) { 
      errors.subStatus = "Status is required.";
    }
    return errors;
  };

  // Submit order creation
  const createManualOrder = async () => {
    setFieldErrors({});

    // Validate required fields
    const errors = validateForm();
    if (Object.keys(errors).length > 0) {
      setFieldErrors(errors);
      return;
    }

    // Set default expiry date to one month later if not entered
    const expires =
      orderData.expires ||
      (() => {
        const date = new Date();
        date.setMonth(date.getMonth() + 1);
        return date.toISOString().split("T")[0];
      })();

    try {
      const orderPayload = {
        widget: {
          title: orderData.title,
          widgetType: orderData.widgetType,
          userId: orderData.userId,
          expires: expires,
          subStatus: orderData.subStatus,
          payment: [],
          htmlData: "",
          subStatus: orderData.subStatus
        },
      };

      // API call to create order
      const response = await apiInstance.post("/widget", orderPayload);

      // Find and attach user details to the response
      const user = availableUsers.find(
        (user) => user.id === response.data.widget.userId
      );
      if (user) {
        response.data.widget.userId = {
          ...user,
          id: user.id,
          displayName: user.displayName,
          email: user.email,
        };
      }

      // Initialize payment details
      response.data.widget._id = response.data.widget.id;

      // Update parent component and close popup
      addOrder(response.data.widget);
      closePopup();
    } catch (error) {
      // Handle validation errors
      if (error.response?.status === 400) {
        setFieldErrors(error.response.data.fieldErrors || {});
      } else {
        console.error("Order creation failed:", error);
      }
    }
  };

  // Add new user to dropdown list
  const addUser = (user) => {
    setAvailableUsers((prevUsers) => [...prevUsers, user]);
  };

  return (
    <div className="mjwidev-manual-order-pp mjwidev-pp">
      <h2>Create Manual Order</h2>

      <form className="mjwi-row">
        <div className="mjwi-col100">
          {/* Widget Name Field */}
          <div className="mjwidev-input-field">
            <label>Widget Name:</label>
            <input
              type="text"
              placeholder="Enter Widget Name"
              name="title"
              value={orderData.title}
              onChange={handleInputChange}
              required
            />
            {fieldErrors.title && (
              <span className="mjwi-error">{fieldErrors.title}</span>
            )}
          </div>

          {/* Widget Type Dropdown */}
          <div className="mjwidev-input-field custom-select-wrapper">
            <label>Widget Type:</label>
            <select
              name="widgetType"
              value={orderData.widgetType}
              onChange={handleInputChange}
              required
            >
              <option value="">Select Widget Type</option>
              {availableWidgets.map((widget) => (
                <option key={widget.id} value={widget.id}>
                  {widget.name}
                </option>
              ))}
            </select>
            <div className="custom-arrow">
              <svg
                fill="#ffffff"
                height="16px"
                width="16px"
                viewBox="0 0 330 330"
                xmlns="http://www.w3.org/2000/svg"
              >
                <path
                  d="M325.607,79.393c-5.857-5.857-15.355-5.858-21.213,0.001l-139.39,139.393L25.607,79.393 
                c-5.857-5.857-15.355-5.858-21.213,0.001c-5.858,5.858-5.858,15.355,0,21.213l150.004,150c2.813,2.813,6.628,4.393,10.606,4.393 
                s7.794-1.581,10.606-4.394l149.996-150C331.465,94.749,331.465,85.251,325.607,79.393z"
                ></path>
              </svg>
            </div>
            {fieldErrors.widgetType && (
              <span className="mjwi-error">{fieldErrors.widgetType}</span>
            )}
          </div>

          {/* User Selection */}
          <div className="mjwidev-input-field custom-select-wrapper">
            <label>User:</label>
            <select
              name="userId"
              value={orderData.userId}
              onChange={handleInputChange}
              required
            >
              <option value="">Select User</option>
              {availableUsers.map((user) => (
                <option key={user.id} value={user.id}>
                  {user.email}
                </option>
              ))}
            </select>
            <div className="custom-arrow">
              <svg
                fill="#ffffff"
                height="16px"
                width="16px"
                viewBox="0 0 330 330"
                xmlns="http://www.w3.org/2000/svg"
              >
                <path
                  d="M325.607,79.393c-5.857-5.857-15.355-5.858-21.213,0.001l-139.39,139.393L25.607,79.393 
                  c-5.857-5.857-15.355-5.858-21.213,0.001c-5.858,5.858-5.858,15.355,0,21.213l150.004,150c2.813,2.813,6.628,4.393,10.606,4.393 
                  s7.794-1.581,10.606-4.394l149.996-150C331.465,94.749,331.465,85.251,325.607,79.393z"
                ></path>
              </svg>
            </div>
            {fieldErrors.userId && (
              <span className="mjwi-error">{fieldErrors.userId}</span>
            )}
          </div>
          <Button
            name="Add User"
            eventHandler={() => popupModalRef.current.openPopup("addUser")}
            className="mjwi-btn-secondary Cancel"
          />
          {/* Status Dropdown */}
           <div className="mjwidev-input-field custom-select-wrapper">
            <label>Status:</label>
            <select
              name="subStatus"
              value={orderData.subStatus}
              onChange={handleInputChange}
              required
            >
              <option value="">Select Status</option>
              <option value="active">Active</option>
              <option value="incomplete">Incomplete</option>
              <option value="expired">Expired</option>
              <option value="past_due">Past Due</option>
              <option value="canceled">Canceled</option>
            </select>
            <div className="custom-arrow">
              <svg
                fill="#ffffff"
                height="16px"
                width="16px"
                viewBox="0 0 330 330"
                xmlns="http://www.w3.org/2000/svg"
              >
                <path d="M325.607,79.393c-5.857-5.857-15.355-5.858-21.213,0.001l-139.39,139.393L25.607,79.393 
                c-5.857-5.857-15.355-5.858-21.213,0.001c-5.858,5.858-5.858,15.355,0,21.213l150.004,150c2.813,2.813,6.628,4.393,10.606,4.393 
                s7.794-1.581,10.606-4.394l149.996-150C331.465,94.749,331.465,85.251,325.607,79.393z"></path>
              </svg>
            </div>
            {fieldErrors.subStatus && (
              <span className="mjwi-error">{fieldErrors.subStatus}</span>
            )}
          </div>
          {/* Expiry Date Field */}
          <div className="mjwidev-input-field">
            <label>Expiry Date:</label>
            <input
              type="date"
              name="expires"
              value={orderData.expires}
              onChange={handleInputChange}
            />
            {fieldErrors.expires && (
              <span className="mjwi-error">{fieldErrors.expires}</span>
            )}
          </div>
        </div>
      </form>

      {/* Action Buttons */}
      <div className="mjwidev-bottom-row">
        <Button name="Create Order" eventHandler={createManualOrder} />
        <Button name="Cancel" eventHandler={closePopup} />
      </div>

      {/* Popup Modal for adding users */}
      <PopupModal ref={popupModalRef} addUser={addUser} />
    </div>
  );
});

export default AddOrderPopup;
